import { z } from "zod";

/**
 * Phase 8d — wire schema for R-AI-3 room-device-scan responses. The AI
 * proposes one suggestion per device it sees in a room photo
 * (receptacle, switch, light fixture, hardwired appliance, …). Each
 * proposal carries a target room (typically the user-supplied roomId
 * for the photo, but the model may also disambiguate when a single
 * scan spans an open kitchen/dining shot), a template-key best guess,
 * a name suggestion, and optional `locationWithinRoom` and `nameplate`
 * hints when the model can read them.
 *
 * Schema content is intentionally *only* what the seam needs to
 * validate. The apply-to-domain mapping (`applyRoomDeviceScan`) routes
 * accepted proposals through the `device` smart factory, where the
 * full set of cross-aggregate invariants (template existence, room
 * presence, cross-site rejection, duplicate circuit memberships) re-
 * fires. The schema accepts any non-empty string for `templateKey`
 * (the template lookup happens at apply time) so a newly-introduced
 * template name doesn't break wire validation ahead of its registry
 * landing.
 *
 * Per R-AI-3 / R-DEVICE-5, room-scan proposals pass through
 * `annotateRoomDeviceScan` (see `room-device-scan.ts` under
 * `packages/domain/src/ai/`) *before* the suggestion set reaches the
 * UI; each suggestion is annotated with either `new` or
 * `likely-existing(deviceId, score)` so high-confidence duplicates
 * render as "already in this room — link?" rather than as new
 * candidates. The annotation is a Phase 8d concern that bolts on top
 * of the wire-parsed `SuggestionSet`; the wire schema itself does not
 * carry annotation state.
 */

const nameplateProposalSchema = z.object({
  manufacturer: z.string().min(1),
  model: z.string().min(1),
  serial: z.string().min(1).optional(),
  year: z.string().min(1).optional(),
});

/**
 * One device-shaped proposal from a room photo. `roomId` is the model's
 * best guess of which room the device belongs to — typically the same
 * as the request's target room, but the schema admits the model
 * disambiguating across an open floor plan when the scan accidentally
 * picks up a device in an adjacent room.
 *
 * `templateKey` is treated as opaque-by-the-schema; the apply step
 * runs `deviceTemplate(templateKey)` and rejects an unknown key
 * through the `device` smart factory's `UNKNOWN_TEMPLATE_KEY` branch.
 */
export const roomDeviceScanProposalSchema = z.object({
  kind: z.literal("device-read"),
  roomId: z.string().min(1),
  templateKey: z.string().min(1),
  name: z.string().min(1),
  locationWithinRoom: z.string().min(1).optional(),
  nameplate: nameplateProposalSchema.optional(),
});

export type RoomDeviceScanProposal = z.infer<typeof roomDeviceScanProposalSchema>;
export type RoomDeviceNameplateProposal = z.infer<typeof nameplateProposalSchema>;
